/**
 * useMountOnFirstOpen — mount on the first open, keep mounted afterwards, and
 * let that first open actually animate in.
 *
 * WHY THIS HOOK EXISTS (Welle 4, Karte 5)
 * ---------------------------------------
 * `App.js` renders two wizard trees (`NewProductWizard` and
 * `UnifiedCreateWizard mode="variants"`) OUTSIDE `<Routes>` — so on every
 * route. On the mobile branch the `SwipeableDrawer` keeps its content in the
 * DOM via `keepMounted: true` (hidden, but present). That costs two complete
 * wizard trees per page before anyone has ever opened the wizard.
 *
 * 🔴 **The obvious move `{open && <Wizard …/>}` is WRONG.** It unmounts
 * immediately on close and therefore cuts the MUI exit animation: the window
 * disappears hard instead of closing (Grill W3, 2026-10-07).
 *
 * The shape that has both: don't mount at all until first use — and keep it
 * mounted afterwards, so `open={false}` runs the normal exit animation. The
 * saving is therefore complete BEFORE first use and zero after; that is the
 * deliberate trade, not a misunderstanding.
 *
 * 🔴 **The first open is TWO-STAGED, and that is the point of the second
 * return value.** MUI's `Drawer` sets `appear` from whether it has already
 * been mounted (`@mui/material/Drawer/Drawer.js`). A `SwipeableDrawer` that is
 * mounted for the very first time directly with `open={true}` therefore has
 * `appear === false`, and its `Slide` mounts already in the ENTERED state: the
 * sheet JUMPS into place instead of sliding in. So on the first open we first
 * mount it CLOSED (`open: false`), let MUI's own mount effect run, and hand
 * the real `open` through one render later. From the second open on, `open`
 * passes straight through — the drawer has been mounted long since and slides
 * as it always did.
 *
 * 🔴 **One state per call site.** `App.js` renders TWO instances of the SAME
 * component (`NewProductWizard.jsx` is a shim over `UnifiedCreateWizard`). A
 * shared value would mount the product wizard too when the variants wizard is
 * opened. A hook carries its state per call site — two calls, two independent
 * values.
 *
 * @param {boolean} open  The wizard's `open` state.
 * @returns {{ mounted: boolean, open: boolean }}
 *   `mounted` — `true` from the first `open === true` on, irrevocably.
 *   `open`    — what the sheet is handed: `false` on the render that mounts it,
 *               the real value one render later (and on every open after that).
 */
import { useEffect, useState } from 'react';

export default function useMountOnFirstOpen(open) {
  // Latches once mounted; keeps the tree in the DOM after a close.
  const [hasMounted, setHasMounted] = useState(false);
  // Turns true one render after mounting — the second stage of the first open.
  const [openPassedThrough, setOpenPassedThrough] = useState(false);

  // Derived, not state: the render on which `open` first turns true must
  // already mount the tree (closed). A state latch would cost one extra render.
  const mounted = hasMounted || open;

  useEffect(() => {
    if (mounted && !hasMounted) setHasMounted(true);
  }, [mounted, hasMounted]);

  useEffect(() => {
    if (mounted && !openPassedThrough) setOpenPassedThrough(true);
  }, [mounted, openPassedThrough]);

  return { mounted, open: mounted && openPassedThrough && open };
}
